export interface ManagedWebsocketConfig {
  /** Display name for logging */
  readonly name: string
  /** Build the WSS URL given a fresh access token */
  readonly path: (opts: { access_token: string }) => string
  /** Fetch a valid access token */
  readonly getAccessToken: () => Promise<string>
  /** Handle an incoming message (called after heartbeat bookkeeping) */
  readonly onMessage: (event: MessageEvent) => void
  /** If set, triggers reconnect when no message arrives within this window */
  readonly heartbeatTimeoutMs?: number
  /** If false, socket closes on window blur and reopens on focus. Default true. */
  readonly allowInBackground?: boolean
  /** Called on clean shutdown (unmount / manual disconnect) */
  readonly onCleanup?: () => void
  /**
   * WebSocket binary frame delivery type.
   * Use 'arraybuffer' when the handler needs synchronous byte access (e.g. PCM audio streaming).
   * Defaults to 'blob', which is the browser's own default.
   */
  readonly binaryType?: BinaryType
}

const MAX_RECONNECT_ATTEMPTS = 10
const INITIAL_RECONNECT_DELAY_MS = 1000
const MAX_RECONNECT_DELAY_MS = 60_000
const NORMAL_CLOSURE = 1000
const POLICY_VIOLATION = 1008

export class ManagedWebsocket {
  protected socket: WebSocket | null = null
  private heartbeatTimer: ReturnType<typeof setTimeout> | undefined
  private reconnectTimer: ReturnType<typeof setTimeout> | undefined
  private reconnectAttempts = 0
  private alive = false
  private readonly config: ManagedWebsocketConfig

  constructor(config: ManagedWebsocketConfig) {
    this.config = config
  }

  private onStatusChange?: (connected: boolean) => void

  setOnStatusChange(callback: (connected: boolean) => void): void {
    this.onStatusChange = callback
  }

  get isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN
  }

  connect(): void {
    this.alive = true
    this.initiateConnection()

    if (this.config.allowInBackground === false) {
      window.addEventListener('focus', this.handleFocus)
      window.addEventListener('blur', this.handleBlur)
    }
  }

  disconnect(): void {
    this.alive = false
    this.clearTimers()
    this.socket?.close(NORMAL_CLOSURE, `${this.config.name} disconnected`)
    this.socket = null
    window.removeEventListener('focus', this.handleFocus)
    window.removeEventListener('blur', this.handleBlur)
    this.config.onCleanup?.()
    this.onStatusChange?.(false)
  }

  private async initiateConnection(): Promise<void> {
    if (!this.alive) return

    let accessToken: string
    try {
      accessToken = await this.config.getAccessToken()
    } catch (error) {
      // Once the session has ended, the token service rejects until the user signs in again, so
      // back off the way a refused socket does instead of letting each attempt reject unhandled.
      console.warn(`${this.config.name}: could not fetch an access token`, error)
      this.scheduleReconnect()
      return
    }
    if (!this.alive) return

    this.socket?.close(NORMAL_CLOSURE)
    this.clearTimers()

    const ws = new WebSocket(this.config.path({ access_token: accessToken }))
    if (this.config.binaryType !== undefined) {
      ws.binaryType = this.config.binaryType
    }

    ws.onopen = () => {
      this.reconnectAttempts = 0
      this.onStatusChange?.(true)
      this.resetHeartbeat()
    }

    ws.onclose = (event) => {
      this.onStatusChange?.(false)
      if (event.code !== NORMAL_CLOSURE && event.code !== POLICY_VIOLATION) {
        this.scheduleReconnect()
      }
    }

    ws.onerror = (error) => {
      console.error(`${this.config.name} WebSocket error:`, error)
    }

    ws.onmessage = (event) => {
      this.resetHeartbeat()
      this.config.onMessage(event)
    }

    this.socket = ws
  }

  private scheduleReconnect(): void {
    this.clearTimers()
    if (!this.alive) return

    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      console.error(`${this.config.name}: max reconnection attempts reached`)
      return
    }

    this.reconnectAttempts++
    const delay = Math.min(
      INITIAL_RECONNECT_DELAY_MS * 2 ** (this.reconnectAttempts - 1),
      MAX_RECONNECT_DELAY_MS
    )

    console.log(
      `${this.config.name}: reconnecting in ${Math.round(delay / 1000)}s ` +
        `(attempt ${this.reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})`
    )

    this.reconnectTimer = setTimeout(() => this.initiateConnection(), delay)
  }

  private resetHeartbeat(): void {
    clearTimeout(this.heartbeatTimer)
    const timeout = this.config.heartbeatTimeoutMs
    if (timeout === undefined) return

    this.heartbeatTimer = setTimeout(() => {
      console.warn(`${this.config.name}: heartbeat timeout, reconnecting`)
      this.scheduleReconnect()
    }, timeout)
  }

  private readonly handleFocus = () => {
    if (this.socket?.readyState === WebSocket.CLOSED) {
      this.initiateConnection()
    }
  }

  private readonly handleBlur = () => {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.close(NORMAL_CLOSURE, 'Window blurred')
      this.clearTimers()
    }
  }

  private clearTimers(): void {
    clearTimeout(this.heartbeatTimer)
    clearTimeout(this.reconnectTimer)
  }
}
